import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'

import {
    Button, DialogContent, Dialog, DialogActions, Grid, Stack,
    InputLabel, OutlinedInput, DialogTitle, Alert, MenuItem, Select, FormControl
} from '@mui/material'

import * as Yup from 'yup'
import { Formik } from 'formik'
import { useSnackbar } from 'notistack'

import AnimateButton from 'components/@extended/AnimateButton'
import { useEventBusContext } from 'services/provider/EventBusProvider'
import { EVENT_BANNER_SLIDE_UPDATE_INIT, EVENT_BANNER_SLIDE_UPDATE_SUCCESS } from 'models/events/banner.event'
import { bannerSlideEdit } from 'models/banner'
import { FormFieldError } from 'components/Form'
import ImageUpload from 'components/common/ImageUpload'
import { BANNER_STATUSES } from 'constants/banner'

export const BannerSlideEditDialog = () => {
    const [slide, setSlide] = useState(null)
    const eventbus = useEventBusContext()

    useEffect(() => {
        // payload is now the full slide object (from Banner.Detail's already-loaded list)
        return eventbus.on(EVENT_BANNER_SLIDE_UPDATE_INIT, (slideData) => setSlide(slideData))
    }, [eventbus])

    return (
        <Dialog open={slide != null} scroll={'body'} maxWidth="xs" fullWidth onClose={() => setSlide(null)}>
            {slide && (
                <BannerSlideEdit
                    slide={slide}
                    onClose={() => setSlide(null)}
                    onSuccess={(data) => {
                        eventbus.fire(EVENT_BANNER_SLIDE_UPDATE_SUCCESS, data)
                        setSlide(null)
                    }}
                />
            )}
        </Dialog>
    )
}

const validation = Yup.object().shape({
    title: Yup.string().max(255).required('Title is required'),
    description: Yup.string().nullable(),
    link: Yup.string().nullable(),
    status: Yup.string().oneOf(BANNER_STATUSES.map((i) => i.value)).required('Status is required')
})

const BannerSlideEdit = ({ slide, onClose, onSuccess }) => {
    const { enqueueSnackbar } = useSnackbar()
    const [imageFile, setImageFile] = useState(null)
    const [imagePreview, setImagePreview] = useState(slide.image_url || null)

    const handleImageChange = (e) => {
        const file = e.target.files?.[0]
        if (file) {
            setImageFile(file)
            const reader = new FileReader()
            reader.onloadend = () => setImagePreview(reader.result)
            reader.readAsDataURL(file)
        }
    }

    const handleImageRemove = () => {
        setImageFile(null)
        setImagePreview(null)
    }

    const onSubmit = async (values, { setErrors }) => {
        const submitData = { ...values }
        if (imageFile) submitData.image = imageFile

        await bannerSlideEdit(slide.id, submitData)
            .then((res) => {
                onSuccess(res.data)
                enqueueSnackbar(res.message || 'Slide updated successfully', { variant: 'success', autoHideDuration: 1500 })
            })
            .catch((res) => {
                if (res.errors) {
                    res.errors['submit'] = res.message
                    setErrors(res.errors)
                } else {
                    setErrors({ submit: res.message })
                }
                enqueueSnackbar(res.message || 'Failed to update slide', { variant: 'error', autoHideDuration: 1500 })
            })
    }

    return (
        <Formik
            initialValues={{
                title: slide.title ?? '',
                description: slide.description ?? '',
                link: slide.link ?? '',
                status: slide.status ?? 'active'
            }}
            validationSchema={validation}
            onSubmit={onSubmit}
        >
            {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
                <form noValidate onSubmit={handleSubmit}>
                    <DialogTitle>Edit Slide</DialogTitle>
                    <DialogContent dividers>
                        <Grid container spacing={3}>
                            {errors.submit && (
                                <Grid item size={{ xs: 12 }}>
                                    <Alert severity="error">{errors.submit}</Alert>
                                </Grid>
                            )}

                            <Grid item size={{ xs: 12 }}>
                                <Stack spacing={1}>
                                    <InputLabel htmlFor="slide-title">Title *</InputLabel>
                                    <OutlinedInput id="slide-title" name="title" value={values.title} onBlur={handleBlur} onChange={handleChange} fullWidth error={Boolean(touched.title && errors.title)} />
                                    <FormFieldError touched={touched.title} errors={errors.title} />
                                </Stack>
                            </Grid>

                            <Grid item size={{ xs: 12 }}>
                                <Stack spacing={1}>
                                    <InputLabel htmlFor="slide-link">Link</InputLabel>
                                    <OutlinedInput id="slide-link" name="link" value={values.link} onBlur={handleBlur} onChange={handleChange} fullWidth error={Boolean(touched.link && errors.link)} />
                                    <FormFieldError touched={touched.link} errors={errors.link} />
                                </Stack>
                            </Grid>

                            <Grid item size={{ xs: 12, md: 12 }}>
                                <Stack spacing={1}>
                                    <InputLabel htmlFor="slide-status">Status *</InputLabel>
                                    <FormControl fullWidth error={Boolean(touched.status && errors.status)}>
                                        <Select id="slide-status" name="status" value={values.status} onChange={handleChange} onBlur={handleBlur}>
                                            {BANNER_STATUSES.map((i) => (<MenuItem key={i.value} value={i.value}>{i.label}</MenuItem>))}
                                        </Select>
                                    </FormControl>
                                    <FormFieldError touched={touched.status} errors={errors.status} />
                                </Stack>
                            </Grid>

                            <Grid item size={{ xs: 12 }}>
                                <Stack spacing={1}>
                                    <InputLabel htmlFor="slide-description">Description</InputLabel>
                                    <OutlinedInput id="slide-description" name="description" value={values.description} onBlur={handleBlur} onChange={handleChange} fullWidth multiline rows={2} error={Boolean(touched.description && errors.description)} />
                                    <FormFieldError touched={touched.description} errors={errors.description} />
                                </Stack>
                            </Grid>

                            <Grid item size={{ xs: 12 }}>
                                <Stack spacing={1}>
                                    <InputLabel>Image</InputLabel>
                                    <ImageUpload imagePreview={imagePreview} onImageChange={handleImageChange} onImageRemove={handleImageRemove} id="slide-image-upload" />
                                </Stack>
                            </Grid>
                        </Grid>
                    </DialogContent>

                    <DialogActions sx={{ p: 1.5 }}>
                        <AnimateButton>
                            <Button disabled={isSubmitting} onClick={onClose} variant="outlined" color="secondary">Cancel</Button>
                        </AnimateButton>
                        <AnimateButton>
                            <Button disabled={isSubmitting} type="submit" variant="contained" color="primary">
                                {isSubmitting ? 'Updating...' : 'Update'}
                            </Button>
                        </AnimateButton>
                    </DialogActions>
                </form>
            )}
        </Formik>
    )
}

BannerSlideEdit.propTypes = { slide: PropTypes.object.isRequired, onClose: PropTypes.func, onSuccess: PropTypes.func }

export default BannerSlideEdit